import { useCallback, useEffect, useState } from "react";
import { useWidgetVariant } from "@/contexts/widget-variant";
import {
  getWidgetStateChangeEvent,
  getWidgetOpenEvent,
  getWidgetCloseEvent,
  getStorageKey,
} from "@/constants/widget-events";

let usePersistence = false;
let initialDefaultOpen = false;

export const initWidgetOpen = (
  defaultOpen: boolean = false,
  persist: boolean = false
): void => {
  usePersistence = persist;
  initialDefaultOpen = defaultOpen;
};

export const useWidgetOpen = (
  defaultValue: boolean = false
): [boolean, (value: boolean) => void] => {
  const variant = useWidgetVariant();
  const stateChangeEvent = getWidgetStateChangeEvent(variant);
  const openEvent = getWidgetOpenEvent(variant);
  const closeEvent = getWidgetCloseEvent(variant);
  const storageKey = getStorageKey(variant);

  const [isOpen, setIsOpenState] = useState<boolean>(() => {
    if (usePersistence && typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) return stored === "true";
    }
    return defaultValue ?? initialDefaultOpen;
  });

  useEffect(() => {
    const handleStateChange = (e: Event): void => {
      const customEvent = e as CustomEvent<boolean>;
      setIsOpenState(customEvent.detail);
    };

    window.addEventListener(stateChangeEvent, handleStateChange as EventListener);

    if (usePersistence) {
      const handleStorageChange = (e: StorageEvent): void => {
        if (e.key === storageKey && e.newValue !== null) {
          setIsOpenState(e.newValue === "true");
        }
      };
      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener(
          stateChangeEvent,
          handleStateChange as EventListener
        );
        window.removeEventListener("storage", handleStorageChange);
      };
    }

    return () => {
      window.removeEventListener(
        stateChangeEvent,
        handleStateChange as EventListener
      );
    };
  }, [stateChangeEvent, storageKey]);

  const setIsOpen = useCallback(
    (value: boolean): void => {
      setIsOpenState(value);
      window.dispatchEvent(
        new CustomEvent(stateChangeEvent, { detail: value })
      );
      if (value) {
        window.dispatchEvent(new CustomEvent(openEvent));
      } else {
        window.dispatchEvent(new CustomEvent(closeEvent));
      }
      if (usePersistence && typeof window !== "undefined") {
        localStorage.setItem(storageKey, String(value));
      }
    },
    [stateChangeEvent, openEvent, closeEvent, storageKey]
  );

  return [isOpen, setIsOpen];
};

export const useWidget = (): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
} => {
  const [isOpen, setIsOpen] = useWidgetOpen();

  const open = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return { isOpen, open, close };
};

export const useSetWidgetOpen = (): {
  closeWidget: () => void;
  openWidget: () => void;
} => {
  const [, setIsOpen] = useWidgetOpen();

  const openWidget = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const closeWidget = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return { closeWidget, openWidget };
};
